import { useQuery } from '@tanstack/react-query';
import {
  fetchCostSummary,
  fetchCostByService,
  fetchCostTrends,
  fetchCostAnomalies,
  fetchCostByCategory,
  fetchCostByServiceEnv,
  fetchUnattributedCosts,
} from '@/api/costs';
import type { CostPeriodParams } from '@/api/costs';

function periodDaysToParams(periodDays?: number | CostPeriodParams): CostPeriodParams | undefined {
  if (periodDays == null) return undefined;
  if (typeof periodDays === 'object') return periodDays;
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - periodDays);
  return {
    period_start: start.toISOString().split('T')[0],
    period_end: end.toISOString().split('T')[0],
  };
}

export function useCostSummary(periodDays?: number | CostPeriodParams) {
  const params = periodDaysToParams(periodDays);
  return useQuery({
    queryKey: ['cost-summary', params],
    queryFn: () => fetchCostSummary(params),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCostByService(periodDays?: number | CostPeriodParams) {
  const params = periodDaysToParams(periodDays);
  return useQuery({
    queryKey: ['cost-by-service', params],
    queryFn: () => fetchCostByService(params),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCostByCategory(periodDays?: number | CostPeriodParams) {
  const params = periodDaysToParams(periodDays);
  return useQuery({
    queryKey: ['cost-by-category', params],
    queryFn: () => fetchCostByCategory(params),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCostTrends(periodDays?: number | CostPeriodParams) {
  const params = periodDaysToParams(periodDays);
  return useQuery({
    queryKey: ['cost-trends', params],
    queryFn: () => fetchCostTrends(params),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCostAnomalies(periodDays?: number | CostPeriodParams) {
  const params = periodDaysToParams(periodDays);
  return useQuery({
    queryKey: ['cost-anomalies', params],
    queryFn: () => fetchCostAnomalies(params),
    staleTime: 10 * 60 * 1000,
  });
}

export function useCostByServiceEnv(
  periodDays?: number | CostPeriodParams,
  group?: string,
) {
  const params = { ...periodDaysToParams(periodDays), ...(group ? { group } : {}) };
  return useQuery({
    queryKey: ['cost-by-service-env', params],
    queryFn: () => fetchCostByServiceEnv(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUnattributedCosts(periodDays?: number | CostPeriodParams) {
  const params = periodDaysToParams(periodDays);
  return useQuery({
    queryKey: ['cost-unattributed', params],
    queryFn: () => fetchUnattributedCosts(params),
    staleTime: 5 * 60 * 1000,
  });
}

